export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Rejects with a TimeoutError if `promise` has not settled within `ms`.
 *
 * Firebase requests go out behind an App Check token. When the reCAPTCHA
 * script cannot load — blocked by CSP, unreachable, wrong site key, a
 * corporate firewall — that token is never issued and the underlying request
 * is never made, so the promise never settles and the caller waits forever
 * with nothing to show the user. Bounding the wait turns that into an error
 * the UI can recover from.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(message)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
